import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, Tag, Clock, Euro, User, Plus, Loader2, AlignLeft } from 'lucide-react';
import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import CustomSelect from '../ui/CustomSelect';
import { useEvents } from '@/contexts/EventsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface NewServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    organizationId: string;
}

const NewServiceModal: React.FC<NewServiceModalProps> = ({ isOpen, onClose, organizationId }) => {
    const { t } = useTranslation();
    const { insertPerformance, categories } = useEvents();
    const { users } = useUsers();
    const { organization } = useAuth();

    const [isSaving, setIsSaving] = useState(false);
    const [showDescription, setShowDescription] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        staffIds: [] as string[],
        price: '',
        duration: organization?.step?.toString() || '30'
    });

    // Staff attivi selezionabili (multi-select: un servizio può essere svolto da più persone)
    const activeStaff = useMemo(
        () => users.filter((u: any) => u.is_active !== false),
        [users]
    );

    const toggleStaff = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            staffIds: prev.staffIds.includes(id)
                ? prev.staffIds.filter((s) => s !== id)
                : [...prev.staffIds, id],
        }));
    };

    const allSelected = activeStaff.length > 0 && formData.staffIds.length === activeStaff.length;
    const toggleAllStaff = () => {
        setFormData((prev) => ({
            ...prev,
            staffIds: allSelected ? [] : activeStaff.map((u: any) => u.id),
        }));
    };

    const isFormValid =
        formData.name.trim() !== '' &&
        formData.categoryId !== '' &&
        formData.staffIds.length > 0 &&
        formData.price !== '' &&
        formData.duration !== '';

    const handleSubmit = async () => {
        if (!isFormValid) return;
        setIsSaving(true);

        try {
            const payload = {
                shop_id: organizationId,
                name: formData.name.trim(),
                description: formData.description.trim(),
                category_id: parseInt(formData.categoryId),
                price: parseFloat(formData.price),
                duration: parseInt(formData.duration)
            };

            // Un servizio può essere assegnato a più membri dello staff in una sola mossa
            for (const sid of formData.staffIds) {
                const res = await insertPerformance({ ...payload, hairdresser_id: sid });
                if (!res.success) throw new Error(res.error);
            }
            toast.success(
                formData.staffIds.length > 1
                    ? `Servizio assegnato a ${formData.staffIds.length} membri dello staff!`
                    : t('salon.services.toast.create_success')
            );

            setFormData({ name: '', description: '', categoryId: '', staffIds: [], price: '', duration: organization?.step?.toString() || '30' });
            setShowDescription(false);
            onClose();

        } catch (error: any) {
            console.error(error);
            toast.error(error.message || t('salon.services.toast.create_error'));
        } finally {
            setIsSaving(false);
        }
    };

    const categoryOptions = categories.map((c: any) => ({
        value: c.id.toString(),
        label: c.name
    }));

    const durationOptions = useMemo(() => {
        const step = organization?.step || 30;
        return Array.from({ length: 12 }, (_, i) => (i + 1) * step).map(m => ({ value: m.toString(), label: `${m} min` }));
    }, [organization?.step]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('salon.services.new_service')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('salon.services.form.new_service_desc')}</p>
                </div>
            }
        >
            <div className="space-y-6">

                {/* Input Nome */}
                <div className="space-y-2 relative z-[50]">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Scissors size={14} /> {t('salon.services.form.name_label')}
                    </label>
                    <input
                        type="text"
                        placeholder={t('salon.services.form.name_placeholder')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 outline-none font-medium text-gray-900"
                    />
                </div>

                {showDescription ? (
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <AlignLeft size={14} /> Descrizione (opzionale)
                        </label>
                        <textarea
                            placeholder="Inserisci una descrizione per questo servizio..."
                            value={formData.description}
                            maxLength={250}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 outline-none font-medium text-gray-900 resize-none h-24"
                        />
                        <div className="flex justify-end items-center mt-1">
                            <p className={`text-[10px] font-bold ${formData.description.length >= 250 ? 'text-red-500' : 'text-gray-400'}`}>
                                {formData.description.length} / 250
                            </p>
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowDescription(true)}
                        className="text-[#d96c4f] font-bold text-sm flex items-center gap-1 hover:opacity-80 transition-opacity"
                    >
                        + Aggiungi descrizione
                    </button>
                )}

                {/* SELECT CATEGORIA */}
                <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Tag size={14} /> {t('salon.services.form.category_label')}
                    </label>
                    <CustomSelect
                        value={formData.categoryId}
                        onChange={(val) => setFormData({ ...formData, categoryId: val })}
                        options={categoryOptions}
                        placeholder={t('common.select')}
                    />
                </div>

                {/* CHI EFFETTUA IL SERVIZIO — multi-select (assegnazione in una mossa) */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <User size={14} /> Chi effettua questo servizio
                        </label>
                        {activeStaff.length > 0 && (
                            <button
                                type="button"
                                onClick={toggleAllStaff}
                                className="text-[11px] font-bold text-clip-600 hover:text-clip-800"
                            >
                                {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                            </button>
                        )}
                    </div>
                    <div className="border border-gray-200 rounded-xl bg-gray-50 max-h-44 overflow-y-auto divide-y divide-gray-100">
                        {activeStaff.length === 0 && (
                            <p className="text-sm text-gray-400 p-3">Nessun membro dello staff attivo.</p>
                        )}
                        {activeStaff.map((u: any) => {
                            const checked = formData.staffIds.includes(u.id);
                            return (
                                <label
                                    key={u.id}
                                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-clip-50/60' : 'hover:bg-gray-100/60'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleStaff(u.id)}
                                        className="w-4 h-4 accent-clip-500 rounded"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        {u.first_name} {u.last_name}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-gray-400">
                        Seleziona uno o più membri: il servizio verrà creato per ciascuno in un colpo solo.
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Euro size={14} /> {t('salon.services.form.price_label')}
                        </label>
                        <input
                            type="number"
                            placeholder="25.00"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 outline-none font-medium text-gray-900"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Clock size={14} /> {t('salon.services.form.duration_label')}
                        </label>
                        <CustomSelect
                            value={formData.duration}
                            onChange={(val) => setFormData({ ...formData, duration: val })}
                            options={durationOptions}
                            placeholder={t('salon.services.form.duration_placeholder')}
                        />
                    </div>
                </div>

                <div className="pt-6 mt-2 border-t border-gray-100 flex gap-4">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                        {t('common.cancel')}
                    </button>
                    <PrimaryButton
                        label={isSaving ? t('common.saving') : t('common.create')}
                        icon={isSaving ? Loader2 : Plus}
                        className={`flex-1 justify-center cursor-pointer ${!isFormValid ? 'opacity-50 cursor-not-allowed hover:scale-100 shadow-none' : ''}`}
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSaving}
                    />
                </div>

            </div>
        </Modal>
    );
};

export default NewServiceModal;